<template>
  <Modal
    v-model:isOpen="isOpen"
    class="delete-account-modal"
    @close="handleClose"
  >
    <template v-if="!isConfirmationModalOpen">
      <!-- Header -->
      <div class="flex flex-col gap-5">
        <div class="flex w-full items-center justify-between">
          <p class="text-size_6 font-bold">Delete Account</p>

          <IconButton
            name="ic-x"
            :c_style="{ type: 'ghost' }"
            @click="handleClose"
          />
        </div>

        <Separator />
      </div>

      <!-- Body -->
      <DeleteAccountContent
        v-model:confirmation-checked="confirmationChecked"
        :show-checkbox
      />

      <!-- Actions -->
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-[25px] md:flex-row">
          <Button
            class="w-full"
            label="Keep Account Open"
            @click="handleClose"
          />

          <Button
            class="w-full"
            label="Request account deletion"
            :c_style="{ name: 'danger' }"
            :disabled="isDeleteButtonDisabled"
            @click="handleDeleteAccount"
          />
        </div>

        <!-- Error Message -->
        <p
          v-if="!!userOrders.activeOrders?.length"
          class="font-light leading-[100%] text-errors"
        >
          All active orders must be completed or canceled before proceeding with
          deletion.
        </p>
      </div>
    </template>

    <ConfirmationModal
      v-else
      v-model:message="message"
      :loading
      :error
      :type
      @submit="handleSubmitFeedback"
      @close="handleConfirmationModalClose"
    />
  </Modal>
</template>

<script setup lang="ts">
import Modal from "~/components/common/Atom/Modal.vue";
import DeleteAccountContent from "~/components/features/UserManagement/Organism/Modals/DeleteAccount/DeleteAccountContent.vue";
import Separator from "~/components/common/Atom/Separator.vue";
import Button from "~/components/common/Atom/Button.vue";
import ConfirmationModal from "~/components/features/UserManagement/Organism/Modals/DeleteAccount/ConfirmationModal.vue";
import useB2bUser from "~/composables/UserManagement/useB2bUser";
import IconButton from "~/components/common/Atom/IconButton.vue";
import paths from "~/constants/paths";
import useOrders from "~/composables/UserManagement/useOrders";

const props = withDefaults(
  defineProps<{
    showCheckbox?: boolean;
    type?: "feedback" | "confirmation";
  }>(),
  {
    showCheckbox: false,
    type: "feedback",
  },
);

const emit = defineEmits(["delete", "submit", "keep"]);

const isOpen = defineModel("isOpen", { default: false });

const isConfirmationModalOpen = ref(false);
const confirmationChecked = ref(false);
const loading = ref(false);
const message = ref<string>("");
const error = ref<string>();

const { addToast } = useToast();
const { userOrders, fetchActiveOrders } = useOrders();
const { logout, deleteAccount } = useB2bUser();
const router = useRouter();

const isDeleteButtonDisabled = computed(() => {
  const hasActiveOrders = !!userOrders.value.activeOrders?.length;

  return hasActiveOrders || (props.showCheckbox && !confirmationChecked.value);
});

const handleDeleteAccount = async () => {
  isConfirmationModalOpen.value = true;
  isOpen.value = false;

  if (props.type === "confirmation") {
    loading.value = true;
    try {
      await deleteAccount("");
    } catch (err: any) {
      const errorMessage =
        err.response?._data?.meta?.errors?.[0]?.message ||
        "Failed to process your request. Please try again.";
      error.value = errorMessage;
      addToast(errorMessage, "error");
    } finally {
      loading.value = false;
    }
  }

  if (!error.value) {
    // Add delay for modal closing transition to finish then open again
    setTimeout(() => {
      isOpen.value = true;
    }, 250);
  }
};

const handleSubmitFeedback = async () => {
  loading.value = true;

  try {
    await deleteAccount(message.value);
    isConfirmationModalOpen.value = false;
    isOpen.value = false;

    logout();
    router.replace(paths.home);

    addToast("Account deletion request submitted successfully.", "success");
  } catch (err: any) {
    error.value =
      err.response?._data?.meta?.errors?.[0]?.message ||
      "Failed to submit feedback. Please try again.";
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  resetStates();
  emit("keep");
};

const handleConfirmationModalClose = () => {
  resetStates();

  if (props.type === "confirmation") {
    logout();
    router.replace(paths.home);
  }
};

const resetStates = () => {
  isConfirmationModalOpen.value = false;
  isOpen.value = false;
  confirmationChecked.value = false;
  loading.value = false;
};

onMounted(async () => {
  await fetchActiveOrders();
});
</script>

<style>
.delete-account-modal {
  .modal-content {
    @apply flex flex-col gap-10 pb-14 pt-7 md:max-w-[621px];
  }
}
</style>
